import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ContainerDash, Icon, Scroll } from "../../styles/global";
import { P3Desk } from "../../styles/styles_text";
import { Button } from "../../components/ui/button";
import NumeroAvaliados from "./NumeroAvaliados";
import DonutChart from "./DonutChart";
import StackedBarChart from "./StackedBarChart";
import EvolutionChart from "./EvolutionChart";
import { useSelector } from "react-redux";
import { CalcAlteracoesEncontradas, CalcAntagonicos, CalcCompValoresReferencia, CalcContralaterais } from "./Funcs";
import AlteracoesEncontradas from "./AlteracoesEncontradas";
import HowWorks from "./HowWorks";
import ListNotes from "./ListNotes";
import { TypesLanguage } from "../../utils/types";
import TableHighPriority from "./TableHighPriorityCases";
import { TextsProps } from "../../database/txt.interface";
import Dots from "../../assets/kinologyIcons/svg/dotthree.svg"
import EditSquare from "../../assets/kinologyIcons/svg/edit_square.svg"
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuShortcut } from "../../components/ui/dropdown-menu";
import { Label } from "recharts";
import AddNote from "./AddNote";

interface TypeExecucoes {
	AGRUPAMENTO_TIPO: string;
	EXECUCAO_EXECMAX: string;
	EXECUCAO_EXECMED: string;
	EXECUCAO_EXECUTADA: string;
	EXECUCAO_MODO: string;
	EXECUCAO_PESOMIN: string;
	EXECUCAO_REPS: string;
	EXECUCAO_RM: number;
	EXECUCAO_TEMPO: string;
	EXECUCAO_TIPO: string;
	EXERCICIO_TITULO_EN: string;
	EXERCICIO_TITULO_ES: string;
	EXERCICIO_TITULO_PT: string;
	ID_AGRUPAMENTO: string;
	ID_EXECUCAO: string;
	ID_EXERCICIO: string;
	ID_USUARIO: string;
	VALOR_ESPERADO: string;
}

interface TypesAvaliacoesUnicas {
	AVALIACAO_FINALIZADA_ATUALIZADA: string;
	AVALIACAO_FINALIZADA_CRIADO: string;
	AVALIACAO_FINALIZADA_EXCLUIDO: string;
	EXECUCOES: Array<TypeExecucoes>;
	IDS_EXECUCOES: string;
	ID_AVALIACAO_FINALIZADA: string;
	ID_EXERCICIO: string;
	ID_PROTOCOLO: string;
	ID_USUARIO: string;
	ID_USUARIO_AVALIADOR: string;
	USUARIO_LADO_DOMINANTE: string;
}

interface TypesPacientesAvaliados {
	ID_USUARIO: number;
	USUARIO_NOME: string;
	PROTOCOLOS: Array<any>
	AVALIACOES: Array<TypesAvaliacoesUnicas>;
}

interface TypesAvaliacoesDynalabs {
	code: number;
	message: string;
	pacientes_avaliados: Array<TypesPacientesAvaliados>;
}

interface TypeAssimetrias {
	ID_USUARIO: number;
	USUARIO_NOME: string;
	ID_CARGO: number;
	LEGENDAS: Array<{
		INFERIOR_DORSAL: any;
		INFERIOR_FRONTAL: any;
		SUPERIOR_DORSAL: any;
		SUPERIOR_FRONTAL: any;
	}>;
	LISTAS: Array<{
		INFERIOR_DORSAL: [];
		INFERIOR_FRONTAL: [];
		SUPERIOR_DORSAL: [];
		SUPERIOR_FRONTAL: [];
	}>;
	RELACOES_REFERENCIAIS: Array<any>;
}

interface TypesAssimetriasDynalabs {
	code: number;
	message: string;
	assimetrias: Array<TypeAssimetrias>;
}

const DashDynaLabs: React.FC = () => {

	const language = useSelector((state: any) => state.configs.language);

	const assimetrias_dynalabs: TypesAssimetriasDynalabs = useSelector(
		(state: any) => state.dashboard.assimetrias_dynalabs
	);

	const avaliacoes_dynalabs: TypesAvaliacoesDynalabs = useSelector(
		(state: any) => state.dashboard.avaliacoes_dynalabs
	);

	const cargos_dynalabs = useSelector(
		(state: any) => state.dashboard.cargos
	);

	const protocolos_dynalabs = useSelector(
		(state: any) => state.manage_protocol.protocolos
	);
	const current_language: TextsProps = useSelector((state: any) => state.configs.current_language);

	const mesesAbreviados = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	const meses_traducoes: any = {
		Jan: { pt: "Jan", es: "Ene" },
		Feb: { pt: "Fev", es: "Feb" },
		Mar: { pt: "Mar", es: "Mar" },
		Apr: { pt: "Abr", es: "Abr" },
		May: { pt: "Mai", es: "May" },
		Jun: { pt: "Jun", es: "Jun" },
		Jul: { pt: "Jul", es: "Jul" },
		Aug: { pt: "Ago", es: "Ago" },
		Sep: { pt: "Set", es: "Sep" },
		Oct: { pt: "Out", es: "Oct" },
		Nov: { pt: "Nov", es: "Nov" },
		Dec: { pt: "Dez", es: "Dic" }
	};

	const ano_atual = new Date().getFullYear();

	const getLastThreeMonthsAbbr = () => mesesAbreviados
		.map((m, i, a) => a[(new Date().getMonth() - 2 + i + 12) % 12]).slice(0, 3);

	const [current_selected_panel, set_current_selected_panel] = useState(getLastThreeMonthsAbbr().reverse()[0])


	const [numero_avaliados, set_numero_avaliados] = useState(0);
	function GenerateNumeroAvaliados() {
		if (avaliacoes_dynalabs?.pacientes_avaliados?.length > 0) {
			set_numero_avaliados(avaliacoes_dynalabs?.pacientes_avaliados?.length);
		}
	}

	const [risco_cargo, set_risco_cargo]: any = useState([]);
	const [default_risco_cargo, set_default_risco_cargo]: any = useState([]);
	function GenerateRiscoCargo(filtros?: any) {

		set_default_risco_cargo(filtros)
		const indicadores_contralaterais_cargo = CalcContralaterais(assimetrias_dynalabs, filtros ? filtros : {});

		const order_by_cargos = cargos_dynalabs.map((cargo: any) => {
			const usuarios = indicadores_contralaterais_cargo?.map((risco: any) => { return risco.usuarios }).flat()
			return usuarios?.filter((user: any) => Number(user.cargo) === Number(cargo.ID_CARGO)).flat();
		})

		const order_by_cargos_clear = order_by_cargos.filter((subarray: any) => subarray.length > 0);

		const order_by_cargos_riscos = order_by_cargos_clear.map((risco: any) => {
			const baixo_risco = risco.filter((user: any) => user.resultado == "POSITIVO")
			const medio_risco = risco.filter((user: any) => user.resultado == "INTERMEDIARIO")
			const alto_risco_1 = risco.filter((user: any) => user.resultado == "NEGATIVO_1")
			const alto_risco_2 = risco.filter((user: any) => user.resultado == "NEGATIVO_2")
			const nome_cargo = cargos_dynalabs.filter((cargo: any) => cargo.ID_CARGO == Number(risco[0].cargo))[0].CARGO_TITULO

			return {
				eixo_x: nome_cargo,
				baixo_risco: baixo_risco.length,
				medio_risco: medio_risco.length,
				alto_risco_1: alto_risco_1.length,
				alto_risco_2: alto_risco_2.length,
				baixo_risco_users: baixo_risco,
				medio_risco_users: medio_risco,
				alto_risco_users_1: alto_risco_1,
				alto_risco_users_2: alto_risco_2,
				usuarios: risco.user_nome,
				total: risco.length,
			}
		})

		set_risco_cargo(order_by_cargos_riscos);
	}

	const [risco_protocolo, set_risco_protocolo]: any = useState([]);
	const [default_risco_protocolo, set_default_risco_protocolo]: any = useState([]);
	function GenerateRiscoProtocolo(filtros?: any) {
		set_default_risco_protocolo(filtros);
		if (protocolos_dynalabs.length > 0 && assimetrias_dynalabs.code === 0) {
			const chart_data = protocolos_dynalabs.map((protocolo: any) => {
				filtros.protocolo = [protocolo.ID_PROTOCOLO]
				const resultado: any = CalcContralaterais(assimetrias_dynalabs, filtros)
				if (resultado[0]?.valor_risco > 0 || resultado[1]?.valor_risco > 0 || resultado[2]?.valor_risco > 0) {
					return {
						eixo_x: protocolo.PROTOCOLO_TITULO_PT,
						baixo_risco: resultado[0]?.valor_risco,
						medio_risco: resultado[1]?.valor_risco,
						alto_risco_1: resultado[2]?.valor_risco,
						alto_risco_2: resultado[3]?.valor_risco,
						baixo_risco_users: resultado[0]?.usuarios,
						medio_risco_users: resultado[1]?.usuarios,
						alto_risco_users_1: resultado[2]?.usuarios,
						alto_risco_users_2: resultado[3]?.usuarios,
					}
				}
			})
			const filtrado = chart_data.filter((item: any) => item !== undefined);
			set_risco_protocolo(filtrado)
		}
	}

	//Alteracoes encontradas

	const [evolucao_alteracoes_encontradas, set_evolucao_alteracoes_encontradas]: any = useState([]);
	function GenerateEvolucaoAlteracoesEncontradas() {

		const mesAtual = new Date().getMonth();
		const mesesAbreviadosFiltrados = mesesAbreviados.slice(0, mesAtual + 1);

		const chart_data = mesesAbreviadosFiltrados.map((mes: string) => {
			const dados_mes: any = CalcAlteracoesEncontradas(assimetrias_dynalabs, { ano: ano_atual, mes: mes })

			if (dados_mes != undefined) {
				return {
					month: mes,
					baixo_risco: dados_mes[0].usuarios.length,
					medio_risco: dados_mes[1].usuarios.length,
					// alto_risco_2: [dados_mes[2].usuarios, dados_mes[3].usuarios].flat().length,
					alto_risco_1: dados_mes[2].usuarios.length,
					alto_risco_2: dados_mes[3].usuarios.length,
					baixo_risco_users: dados_mes[0].usuarios,
					medio_risco_users: dados_mes[1].usuarios,
					// alto_risco_users_2: [dados_mes[2].usuarios, dados_mes[3].usuarios].flat(),
					alto_risco_users_1: dados_mes[2].usuarios,
					alto_risco_users_2: dados_mes[3].usuarios,
				}
			}
		})
		if (chart_data[0] != undefined && chart_data != undefined) {
			set_evolucao_alteracoes_encontradas(chart_data)
		}

	}

	const [alteracoes_encontradas, set_alteracoes_encontradas]: any = useState([])
	const [default_filter_alteracoes_encontradas, set_default_filter_alteracoes_encontradas]: any = useState([]);
	function GenerateAlteracoesEncontradas(filtros?: any) {
		set_default_filter_alteracoes_encontradas(filtros);
		const resultado = CalcAlteracoesEncontradas(assimetrias_dynalabs, filtros ? filtros : {});
		set_alteracoes_encontradas(resultado)
	}

	//Contralaterais
	const [indicadores_contralaterais, set_indicadores_contralaterais]: any = useState([]);
	const [default_filter_contralaterais, set_default_filter_contralaterais]: any = useState({});
	function GenerateIndicadoresContralaterais(filtros?: any) {
		set_default_filter_contralaterais(filtros)
		const resultado: any = CalcContralaterais(assimetrias_dynalabs, filtros ? filtros : {});

		//Agrupa users Alto risco e Alto risco 2 
		resultado.push({
			fill: "#D12600",
			name_risco: "Alto risco",
			usuarios: [resultado[2].usuarios, resultado[3].usuarios].flat(),
			valor_risco: [resultado[2].usuarios, resultado[3].usuarios].flat().length,
		})
		delete resultado[2]
		delete resultado[3]

		if (resultado != undefined) {
			set_indicadores_contralaterais(resultado);
		}
	}

	const [evolucao_indicadores_contralaterais, set_evolucao_indicadores_contralaterais]: any = useState([]);
	function GenerateEvolucaoIndicadoresContralaterais() {

		const mesAtual = new Date().getMonth();
		const mesesAbreviadosFiltrados = mesesAbreviados.slice(0, mesAtual + 1);

		const chart_data = mesesAbreviadosFiltrados.map((mes: string) => {
			const dados_mes: any = CalcContralaterais(assimetrias_dynalabs, { ano: ano_atual, mes: mes })

			if (dados_mes != undefined) {
				return {
					month: mes,
					baixo_risco: dados_mes[0].usuarios.length,
					medio_risco: dados_mes[1].usuarios.length,
					alto_risco_2: [dados_mes[2].usuarios, dados_mes[3].usuarios].flat().length,
					// alto_risco_1: dados_mes[2].usuarios.length,
					// alto_risco_2: dados_mes[3].usuarios.length,
					baixo_risco_users: dados_mes[0].usuarios,
					medio_risco_users: dados_mes[1].usuarios,
					alto_risco_users_2: [dados_mes[2].usuarios, dados_mes[3].usuarios].flat(),
					// alto_risco_users_1: dados_mes[2].usuarios,
					// alto_risco_users_2: dados_mes[3].usuarios,
				}
			}
		})
		if (chart_data[0] != undefined && chart_data != undefined) {
			set_evolucao_indicadores_contralaterais(chart_data)
		}

	}

	//Antagonicos
	const [indicadores_antagonicos, set_indicadores_antagonicos]: any = useState([]);
	const [default_filter_antagonicos, set_default_filter_antagonicos]: any = useState([]);
	function GenerateIndicadoresAntagonicos(filtros?: any) {
		set_default_filter_antagonicos(filtros)
		const resultado = CalcAntagonicos(assimetrias_dynalabs, filtros ? filtros : {});
		if (resultado != undefined) {
			set_indicadores_antagonicos(resultado);
		}
	}

	const [evolucao_indicadores_antagonicos, set_evolucao_indicadores_antagonicos]: any = useState([]);
	function GenerateEvolucaoIndicadoresAntagonicos() {
		const mesAtual = new Date().getMonth();
		const mesesAbreviadosFiltrados = mesesAbreviados.slice(0, mesAtual + 1);

		const chart_data = mesesAbreviadosFiltrados.map((mes: string) => {
			const dados_mes: any = CalcAntagonicos(assimetrias_dynalabs, { ano: ano_atual, mes: mes })

			if (dados_mes != undefined) {
				return {
					month: mes,
					baixo_risco: dados_mes[0].usuarios.length,
					alto_risco_2: dados_mes[1].usuarios.length,
					medio_risco: dados_mes[2].usuarios.length,
					baixo_risco_users: dados_mes[0].usuarios,
					alto_risco_users_2: dados_mes[1].usuarios,
					medio_risco_users: dados_mes[2].usuarios,
				}
			}
		})
		if (chart_data[0] != undefined && chart_data != undefined) {
			set_evolucao_indicadores_antagonicos(chart_data)
		}

	}

	//Comparação de referencias

	const [comparacao_valores_referencia, set_comparacao_valores_referencia]: any = useState([]);
	const [default_filter_valores_referencia, set_default_filter_valores_referencia]: any = useState([]);
	function GenerateCompValoresReferencia(filtros?: any) {
		set_default_filter_valores_referencia(filtros)
		if (avaliacoes_dynalabs.code === 0) {
			const resultado = CalcCompValoresReferencia(avaliacoes_dynalabs, filtros ? filtros : {});
			set_comparacao_valores_referencia(resultado);
		}
	}

	const [evolucao_comparacao_valores_referencia, set_evolucao_comparacao_valores_referencia]: any = useState([]);
	function GenerateEvolucaoCompValoresReferencia() {
		const mesAtual = new Date().getMonth();
		const mesesAbreviadosFiltrados = mesesAbreviados.slice(0, mesAtual + 1);

		if (avaliacoes_dynalabs.code === 0) {
			const chart_data = mesesAbreviadosFiltrados.map((mes: string) => {
				const dados_mes: any = CalcCompValoresReferencia(avaliacoes_dynalabs, { ano: ano_atual, mes: mes })
				if (dados_mes != undefined) {
					return {
						month: mes,
						baixo_risco: dados_mes[0].usuarios.length,
						alto_risco_2: dados_mes[1].usuarios.length,
						medio_risco: dados_mes[2].usuarios.length,
						baixo_risco_users: dados_mes[0].usuarios,
						medio_risco_users: dados_mes[2].usuarios,
						alto_risco_users_2: dados_mes[1].usuarios,
					}
				}
			})
			if (chart_data[0] != undefined && chart_data != undefined) {
				set_evolucao_comparacao_valores_referencia(chart_data)
			}
		}
	}


	const setCurrentPanel = (item: any) => {

		set_current_selected_panel(item);

		GenerateIndicadoresContralaterais({ ano: ano_atual, mes: item });
		GenerateIndicadoresAntagonicos({ ano: ano_atual, mes: item });
		GenerateCompValoresReferencia({ ano: ano_atual, mes: item });
		GenerateAlteracoesEncontradas({ ano: ano_atual, mes: item });
		GenerateRiscoProtocolo({ ano: ano_atual, mes: item });
		GenerateRiscoCargo({ ano: ano_atual, mes: item });
	}

	useEffect(() => {
		GenerateEvolucaoIndicadoresContralaterais();
		GenerateEvolucaoIndicadoresAntagonicos();
		GenerateEvolucaoCompValoresReferencia();
		GenerateEvolucaoAlteracoesEncontradas();
		GenerateNumeroAvaliados();
		setCurrentPanel(current_selected_panel)
	}, []);

	return (
		<ContainerDash style={{ backgroundColor: "#F1F2F3" }}>
			<Frame style={{
				height: "100%",
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
			}}>
				<Cabecalho style={{ width: '60%' }}>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "center",
							width: '100%',
						}}
					>
						<h1 style={{ color: '#63677A', fontSize: '1.5rem' }}>Dashboard Dynalab</h1>
						{/* <AddNote /> */}

					</div>
					<div
						style={{
							display: "flex",
							marginTop: "1.5rem",
							marginBottom: "1rem",
							justifyContent: 'space-between'
						}}
					>
						<div style={{ display: "flex" }}>
							{/* <Button
								style={{
									backgroundColor: "transparent",
									color: "#000E4B",
									padding: "0.5rem 1rem",
									borderRadius: "2rem",
									border: "1px solid #ABADB7",
									fontSize: "0.8rem",
									fontWeight: "500",
									cursor: "pointer",
									marginRight: "1rem",
								}}
							>
								<Icon src={Plus} />
								Criar painel
							</Button> */}
							{getLastThreeMonthsAbbr().reverse().map((item: string, index) => (
								<Button
									key={index}
									onClick={() => setCurrentPanel(item)}
									style={{
										backgroundColor: item == current_selected_panel ? "#00A377" : "#CEF6E7",
										color: item == current_selected_panel ? "#FFFF" : "#000E4B",
										padding: "0.5rem 1rem",
										borderRadius: "2rem",
										border: "none",
										fontSize: "0.8rem",
										fontWeight: "500",
										cursor: "pointer",
										marginRight: "1rem",
									}}
								>
									{language == TypesLanguage.PT ? meses_traducoes[item].pt :
										language == TypesLanguage.ES ? meses_traducoes[item].es :
											meses_traducoes[item]
									}/{ano_atual}

								</Button>
							))}
							<Button
								onClick={() => set_current_selected_panel("anual")}
								style={{
									backgroundColor: current_selected_panel == "anual" ? "#00A377" : "#CEF6E7",
									color: current_selected_panel == "anual" ? "#FFFF" : "#000E4B",
									padding: "0.5rem 1rem",
									borderRadius: "2rem",
									border: "none",
									fontSize: "0.8rem",
									fontWeight: "500",
									cursor: "pointer",
									marginRight: "1rem",
								}}
							>
								Linha do tempo anual

							</Button>
						</div>

						<Popover>
							<PopoverTrigger asChild>
								<img style={{ width: '1.5rem', height: '1.5rem', marginLeft: "1rem" }} src={Dots} alt="" />
							</PopoverTrigger>
							<PopoverContent className="bg-white rounded-[12px]" align="end">
								<AddNote type={"popover"} />
							</PopoverContent>
						</Popover>
					</div>
				</Cabecalho>

				<Scroll style={{
					paddingBottom: "10rem",
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}>
					<div style={{ width: '60%' }}>


						{current_selected_panel != "anual" &&
							<>
								<P3Desk
									style={{ marginTop: "1rem", fontSize: "1.2rem", color: "#9497A4", fontWeight: '300' }}
								>
									Análise de risco mensal - {
										language == TypesLanguage.PT ? meses_traducoes[current_selected_panel].pt :
											language == TypesLanguage.ES ? meses_traducoes[current_selected_panel].es :
												meses_traducoes[current_selected_panel]
									}/{ano_atual}
								</P3Desk>
								<HowWorks />
								{/* <NumeroAvaliados avaliados={numero_avaliados} /> */}


								<div
									style={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "space-between",
									}}
								>
									<AlteracoesEncontradas
										title="Alterações encontradas"
										data={alteracoes_encontradas}
										onDonutChange={GenerateAlteracoesEncontradas}
										defaultFilter={default_filter_alteracoes_encontradas}
									/>
									{/* <DonutChart
									orientation="horizontal"
									title="Indicadores de alterações nos parâmetros"
									data={alteracao_parametros}
									onDonutChange={GenerateAlteracaoParametros}
									defaultFilter={default_filter_alteracao_parametros}
								/> */}

								</div>
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "space-between",
									}}
								>
									<DonutChart
										orientation="vertical"
										title="Indicadores Contralaterais"
										data={indicadores_contralaterais}
										onDonutChange={GenerateIndicadoresContralaterais}
										defaultFilter={default_filter_contralaterais}
									/>
									<DonutChart
										orientation="vertical"
										title="Indicadores Antagônicos"
										data={indicadores_antagonicos}
										onDonutChange={GenerateIndicadoresAntagonicos}
										defaultFilter={default_filter_antagonicos}
									/>
									<DonutChart
										orientation="vertical"
										title="Alteração comparado aos valores de referência"
										data={comparacao_valores_referencia}
										onDonutChange={GenerateCompValoresReferencia}
										defaultFilter={default_filter_valores_referencia}
									/>
								</div>

								<StackedBarChart
									title="Contagem de risco por protocolo"
									labels={[
										{ label: "Sem alterações", color: "#00D084" },
										{ label: "Alterações leves", color: "#FFC530" },
										{ label: "Alterações significativas", color: "#E57E00" },
										{ label: "Alterações graves", color: "#D12600" },
									]}
									chartData={risco_protocolo}
									defaultFilter={default_risco_protocolo}
									onStackedChange={GenerateRiscoProtocolo}
									subtitle="Tempo médio: 48m"
								/>

								<StackedBarChart
									title="Contagem de risco por cargo"
									labels={[
										{ label: "Sem alterações", color: "#00D084" },
										{ label: "Alterações leves", color: "#FFC530" },
										{ label: "Alterações significativas", color: "#E57E00" },
										{ label: "Alterações graves", color: "#D12600" },
									]}
									chartData={risco_cargo}
									defaultFilter={default_risco_cargo}
									onStackedChange={GenerateRiscoCargo}
								/>
								<TableHighPriority
									title={current_language?.titulo_casos_alta_prioridade}
									defaultFilter={default_filter_alteracoes_encontradas}
									data={alteracoes_encontradas}
									onDonutChange={GenerateAlteracoesEncontradas}
									assimetrya={assimetrias_dynalabs}
								/>
							</>
						}



						{/* <EvolutionChart
						title="Indicador de alterações nos parâmetros"
						chartData={evolucao_alteracao_parametros}
						labels={labels_evolution_mock}
						fills={{ fill_baixo_risco: "#0CD100", fill_medio_risco: "#797D8D", fill_alto_risco: "#D12600" }}
					/> */}

						{current_selected_panel == "anual" &&
							<>
								<P3Desk style={{ marginTop: "1rem" }}>Linha do tempo anual</P3Desk>
								<NumeroAvaliados avaliados={numero_avaliados} />

								<EvolutionChart
									title="Alterações encontradas"
									chartData={evolucao_alteracoes_encontradas}
									labels={
										[
											{ label: "Sem alterações", color: "#00D084" },
											{ label: "Alterações leves", color: "#FFC530" },
											{ label: "Alterações significativas", color: "#E57E00" },
											{ label: "Alterações graves", color: "#D12600" },
										]
									}
									fills={{
										fill_baixo_risco: "#0CD100",
										fill_medio_risco: "#FFC530",
										fill_alto_risco_1: "#E57E00",
										fill_alto_risco_2: "#D12600"
									}}
								/>

								<EvolutionChart
									title="Indicadores Contralaterais"
									chartData={evolucao_indicadores_contralaterais}
									labels={
										[
											{ label: "Baixo risco", color: "#00D084" },
											{ label: "Médio risco", color: "#FFC530" },
											{ label: "Alto risco", color: "#D12600" },
										]
									}
									fills={{
										fill_baixo_risco: "#0CD100",
										fill_medio_risco: "#FFC530",
										fill_alto_risco_1: "#E57E00",
										fill_alto_risco_2: "#D12600"
									}}
								/>

								<EvolutionChart
									title="Indicadores Antagônicos"
									chartData={evolucao_indicadores_antagonicos}
									labels={
										[
											{ label: "NA", color: "#797D8D" },
											{ label: "Baixa chance de lesão", color: "#00D084" },
											{ label: "Há chance de lesão", color: "#D12600" },
										]
									}
									fills={{
										fill_baixo_risco: "#0CD100",
										fill_medio_risco: "#797D8D",
										fill_alto_risco_1: "#E57E00",
										fill_alto_risco_2: "#D12600"
									}}
								/>

								<EvolutionChart
									title="Alteração comparado aos valores de referência"
									chartData={evolucao_comparacao_valores_referencia}
									labels={
										[
											{ label: "NA", color: "#797D8D" },
											{ label: "Abaixo do esperado", color: "#D12600" },
											{ label: "Dentro do esperado", color: "#00D084" },
										]
									}
									fills={{
										fill_baixo_risco: "#0CD100",
										fill_medio_risco: "#797D8D",
										fill_alto_risco_1: "#E57E00",
										fill_alto_risco_2: "#D12600"
									}}
								/>
							</>
						}
						<ListNotes />
					</div>
				</Scroll>
			</Frame>
		</ContainerDash>
	);
};

const Frame = styled.div`
	width: 100%;
`;

const Cabecalho = styled.div`
	width: 100%;
	margin-top: 2rem;
`;

export default DashDynaLabs;
